/**
 * Agent Bridge Gateway (Dev Environment Only)
 * Allows Hermes, OpenClaw, and external agents to connect and interact with Guandan game state.
 */
import { GameState, PlayerSeat } from './types';
import { extractObservation, GACPObservation } from './protocol';

type MoveCallback = (action: 'play' | 'pass', cardIds?: string[]) => void;

class AgentBridgeManager {
  private isEnabled: boolean = false;
  private activeSeat: PlayerSeat = 2; // Default to North / Partner
  private agentName: string = 'Hermes-Agent';
  private moveHandler: MoveCallback | null = null;
  private lastObservation: GACPObservation | null = null;

  constructor() {
    if (typeof window !== 'undefined' && (import.meta.env.DEV || window.location.search.includes('agent=true'))) {
      this.isEnabled = true;
      (window as any).__GACP_BRIDGE__ = this;
    }
  }

  public enable(seat: PlayerSeat = 2, name: string = 'Hermes-Agent') {
    this.isEnabled = true;
    this.activeSeat = seat;
    this.agentName = name;
  }

  public disable() {
    this.isEnabled = false;
  }

  public getStatus() {
    return {
      enabled: this.isEnabled,
      activeSeat: this.activeSeat,
      agentName: this.agentName,
      hasLastObservation: this.lastObservation !== null,
    };
  }

  public registerMoveHandler(handler: MoveCallback) {
    this.moveHandler = handler;
  }

  /**
   * Called by the game loop when a turn updates.
   */
  public onTurnUpdate(gameState: GameState) {
    if (!this.isEnabled) return;
    if (gameState.currentTurn === this.activeSeat && gameState.phase === 'playing') {
      const obs = extractObservation(gameState, this.activeSeat);
      this.lastObservation = obs;

      // Dispatch CustomEvent for OpenClaw / Browser Agent
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('gacp_turn_start', { detail: obs }));
      }
    }
  }

  /**
   * External Agent submits move via JS execution or Event
   */
  public submitMove(action: 'play' | 'pass', cardIds: string[] = []) {
    if (this.moveHandler) {
      this.moveHandler(action, cardIds);
    }
  }

  public getObservation(seat: PlayerSeat = this.activeSeat, gameState?: GameState): GACPObservation | null {
    if (gameState) {
      return extractObservation(gameState, seat);
    }
    return this.lastObservation;
  }
}

export const AgentBridge = new AgentBridgeManager();
